import { useState, useEffect, useCallback, useRef } from 'react';
import { backendURL } from '../App';

export const useCsrf = () => {
  const [csrfToken, setCsrfToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const tokenRef = useRef(null);

  const fetchCsrfToken = useCallback(async () => {
    try {
      const response = await fetch(backendURL + '/csrf_token', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const token = data.result.csrf_token;
          setCsrfToken(token);
          tokenRef.current = token;
          return token;
        }
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, []);

  useEffect(() => {
    fetchCsrfToken();
  }, [fetchCsrfToken]);

  const refreshToken = useCallback(() => {
    setIsLoading(true);
    return fetchCsrfToken();
  }, [fetchCsrfToken]);

  /**
   * Wrapper around fetch that includes the CSRF token and auto-retries once on 403.
   * Use this for all state-changing (POST/PUT/DELETE) requests.
   */
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    let token = tokenRef.current;
    if (!token) {
      token = await fetchCsrfToken();
    }

    const doFetch = (t) => fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        ...(t ? { 'X-CSRFToken': t } : {}),
      },
    });

    let response = await doFetch(token);

    // Auto-retry once on CSRF failure (token may have expired)
    if (response.status === 403) {
      const data = await response.clone().json().catch(() => null);
      if (data && data.message && data.message.toLowerCase().includes('security token')) {
        const newToken = await fetchCsrfToken();
        if (newToken) {
          response = await doFetch(newToken);
        }
      }
    }

    return response;
  }, [fetchCsrfToken]);

  return { csrfToken, isLoading, refreshToken, authenticatedFetch };
};
